import { useEffect, useRef, useState } from 'react';

import { useStore } from '@feathr/extender/state';

export interface IOgTagData {
  ogHeadline: string;
  ogDescription: string;
  ogDisplayLink: string;
  /** Image URL from OG tags. Included for future use in creative preview. */
  ogImage: string;
}

export interface IUseOgTagsResult extends IOgTagData {
  isLoading: boolean;
}

const emptyOgData: IOgTagData = {
  ogHeadline: '',
  ogDescription: '',
  ogDisplayLink: '',
  ogImage: '',
};

/**
 * Fetches OG tags for a destination URL via the backend.
 * Calls GET /integrations/meta/og_tags?url=<destinationUrl>.
 * Fails silently on errors.
 *
 * @param destinationUrl - The URL to fetch OG tags for.
 * @returns OG tag data and loading state.
 *
 * @example
 * const { ogHeadline, ogDescription, ogDisplayLink, isLoading } = useOgTags(
 *   creative.get('destination_url'),
 * );
 */
export function useOgTags(destinationUrl: string | undefined): IUseOgTagsResult {
  const { MetaIntegrations } = useStore();
  const [data, setData] = useState<IOgTagData>(emptyOgData);
  const [isLoading, setIsLoading] = useState(false);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (!destinationUrl) {
      setData(emptyOgData);
      return;
    }

    /* Validate URL before fetching */
    try {
      new URL(destinationUrl);
    } catch {
      return;
    }

    /*
     * Increment a fetch ID to discard stale responses when the URL changes rapidly.
     * We use a counter instead of AbortController because wretch doesn't support abort signals.
     */
    const id = ++fetchIdRef.current;
    setIsLoading(true);

    async function fetchOgTags(): Promise<void> {
      try {
        const ogTags = await MetaIntegrations.getOgTags(destinationUrl!);

        if (fetchIdRef.current !== id) {
          return;
        }

        setData({
          ogHeadline: ogTags.headline,
          ogDescription: ogTags.description,
          ogDisplayLink: ogTags.display_link.replace(/^https?:\/\//i, ''),
          ogImage: ogTags.image,
        });
      } catch {
        /* Fail silently */
      } finally {
        if (fetchIdRef.current === id) {
          setIsLoading(false);
        }
      }
    }

    fetchOgTags();
  }, [destinationUrl, MetaIntegrations]);

  return { ...data, isLoading };
}
